// Admin "Instagram" tab — post tracker for the founder page (MongoDB). Data: routes/adminIgContent.js.
import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import AnalysisPanel, { InlineProgress } from "./IgAnalysisPanel";

// Remember the admin's filters/sort for this tab (survives reloads).
const FILTER_KEY = "admindash.ig.filters";
function loadFilters() {
  try { return JSON.parse(localStorage.getItem(FILTER_KEY) || "{}") || {}; } catch { return {}; }
}

const LABELS = ["ugc", "promo", "clip", "music", "other"];
const SORTS = [["postDate", "Newest"], ["views", "Views"], ["outlier", "Outlier score"], ["engagement", "Engagement"], ["likes", "Likes"]];
const CATS = { viral: "Viral", hit: "Hit", above_average: "Above avg", average: "Average", below_average: "Below avg", "n/a": "—" };

const num = (v) => (v == null ? "—" : Number(v).toLocaleString());
const compact = (v) => Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0);
const rel = (d) => {
  if (!d) return null;
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};
const STALE_MS = 13 * 60 * 60 * 1000; // sync runs every 6h — older than ~2 cycles means it stalled
const day = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "—");

function Sparkline({ points }) {
  if (!points || points.length < 2) return <div className="muted" style={{ fontSize: 12 }}>Chart appears after 2+ daily syncs</div>;
  const w = 260, h = 44, max = Math.max(...points), min = Math.min(...points), span = max - min || 1;
  const d = points.map((v, i) => `${(i / (points.length - 1)) * w},${h - 4 - ((v - min) / span) * (h - 8)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline points={d} fill="none" stroke="#ff8a33" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function IgContentTab() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const saved = useRef(loadFilters()).current;
  const [label, setLabel] = useState(saved.label || "");
  const [type, setType] = useState(saved.type ?? "REELS");
  const [sort, setSort] = useState(saved.sort || "postDate");
  useEffect(() => { try { localStorage.setItem(FILTER_KEY, JSON.stringify({ label, type, sort })); } catch { /* no-op */ } }, [label, type, sort]);
  const [job, setJob] = useState(null);       // analysis job status
  const [open, setOpen] = useState(null);     // mediaId whose details are expanded
  const [detail, setDetail] = useState(null); // full doc (with transcript) for the expanded post
  const [dismissed, setDismissed] = useState(null); // finishedAt of the run whose result panel was dismissed
  const openRef = useRef(null); openRef.current = open;
  const seenRef = useRef(0);                  // posts finished so far — when it grows, refresh the list without a reload

  const load = useCallback(() => {
    const params = { sort };
    if (label) params.label = label;
    if (type) params.type = type;
    axiosInstance.get("/admin/ig-content", { params })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.error || "Failed to load Instagram data"); setData({ items: [], accounts: [], labels: [], history: [], summary: {} }); });
  }, [label, type, sort]);
  useEffect(load, [load]);

  const connectUrl = `${axiosInstance.defaults.baseURL}/auth/instagram?token=${encodeURIComponent(localStorage.getItem("token") || "")}`;

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = (await axiosInstance.post("/admin/ig-content/sync")).data;
      if (r.skipped) toast.error(r.skipped === "not_configured" ? "TOKEN_ENCRYPTION_KEY isn't set on the server" : "A sync is already running");
      else if (!r.accounts?.length) toast.error("No connected Instagram account yet");
      else {
        const a = r.accounts[0];
        a.error ? toast.error(a.error) : toast.success(`Synced ${a.posts} posts${a.metricsAvailable ? "" : " (no metrics — reconnect)"}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.error || "Sync failed"); }
    setSyncing(false);
  };

  const setPostLabel = async (item, value) => {
    const prev = data;
    setData({ ...data, items: data.items.map((i) => (i.mediaId === item.mediaId ? { ...i, label: value } : i)) });
    try { await axiosInstance.patch(`/admin/ig-content/${item.mediaId}`, { label: value }); }
    catch { setData(prev); toast.error("Couldn't save label"); }
  };

  // Analysis job: pick up one already running, then poll every second. Each time a post finishes, refresh the
  // table, the "what's working" panel and the open details in place — no page reload needed.
  const refreshDetail = useCallback(async () => {
    const id = openRef.current;
    if (!id) return;
    try { setDetail((await axiosInstance.get(`/admin/ig-content/${id}`)).data.item); } catch { /* keep what we have */ }
  }, []);
  useEffect(() => { axiosInstance.get("/admin/ig-content/analyze/status").then((r) => { setJob(r.data); seenRef.current = r.data.done + r.data.failed; }).catch(() => {}); }, []);
  useEffect(() => {
    if (!job?.running) return undefined;
    const t = setInterval(async () => {
      try {
        const r = (await axiosInstance.get("/admin/ig-content/analyze/status")).data;
        setJob(r);
        const finished = r.done + r.failed;
        if (finished !== seenRef.current || !r.running) { seenRef.current = finished; load(); refreshDetail(); }
        if (!r.running) {
          clearInterval(t);
          if (r.failed) toast.error(`${r.failed} of ${r.total} failed: ${r.errors?.[r.errors.length - 1]?.error || "see the log"}`, { duration: 10000 });
        }
      } catch { /* keep polling */ }
    }, 1000);
    return () => clearInterval(t);
  }, [job?.running, load, refreshDetail]);

  const startAnalyze = async (mediaIds) => {
    try {
      const r = (await axiosInstance.post("/admin/ig-content/analyze", mediaIds ? { mediaIds } : { limit: 10 })).data;
      if (!r.started) return toast("Nothing left to analyze");
      seenRef.current = 0;
      setDismissed(null);
      setJob({ running: true, total: r.total, done: 0, failed: 0, errors: [], log: [{ t: new Date().toISOString(), level: "info", msg: `Started — ${r.total} Reel${r.total === 1 ? "" : "s"} queued` }], stepStates: [], startedAt: new Date().toISOString() });
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.status) { setJob(e.response.data.status); return; }
      toast.error(e.response?.data?.error || "Couldn't start analysis");
    }
  };

  const toggleDetail = async (p) => {
    if (open === p.mediaId) { setOpen(null); return; }
    setOpen(p.mediaId); setDetail(null);
    try { setDetail((await axiosInstance.get(`/admin/ig-content/${p.mediaId}`)).data.item); } catch { setDetail(p); }
  };

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;

  const { items, accounts = [], labels = [], history = [], stat, summary = {}, handle, lastSyncedAt, patterns = {} } = data;
  const an = summary.analysis || {};
  const stale = lastSyncedAt && Date.now() - new Date(lastSyncedAt).getTime() > STALE_MS;
  const hasPatterns = Object.values(patterns).some((l) => l?.length);
  // Show while running, and for 30 min after a run (until dismissed) so the outcome is never missed.
  const showPanel = job && job.total > 0 && (job.running || (job.finishedAt && dismissed !== job.finishedAt && Date.now() - new Date(job.finishedAt).getTime() < 30 * 60 * 1000));
  const acct = accounts.find((a) => a.handle === handle) || accounts[0];
  const needsConnect = !acct || !acct.connected;
  const needsMetrics = acct && acct.connected && !acct.metricsAvailable;
  const cats = summary.categories || {};

  return (
    <div className="admindash-section admindash-fade-in">
      <div className="igc-head">
        <h2 className="admindash-section-title" style={{ margin: 0 }}>
          Instagram {handle && <span className="muted">@{handle}</span>}
        </h2>
        {lastSyncedAt && <span className={`igc-synced ${stale ? "igc-synced--stale" : ""}`}>Last synced {rel(lastSyncedAt)}</span>}
        <div className="igc-actions">
          <button className={`admindash-refresh ${job?.running ? "igc-btn-busy" : ""}`} onClick={() => startAnalyze()} disabled={needsConnect || job?.running || !an.unanalyzed} title="Whisper transcript + GPT hooks/topic + Gemini visuals for each new Reel (10 per click)">
            {job?.running ? <><span className="igc-spin igc-spin--light" />Analyzing {job.done + job.failed}/{job.total}…</> : `Analyze${an.unanalyzed ? ` (${an.unanalyzed} new)` : ""}`}
          </button>
          <button className="admindash-refresh" onClick={syncNow} disabled={syncing || needsConnect}>{syncing ? "Syncing…" : "Sync now"}</button>
          <a className="admindash-refresh" href={connectUrl}>{needsConnect ? "Connect Instagram" : "Reconnect"}</a>
        </div>
      </div>

      {showPanel && <AnalysisPanel job={job} onDismiss={() => setDismissed(job.finishedAt)} />}
      {stale && <div className="igc-banner">Auto-sync hasn't run in {rel(lastSyncedAt).replace(" ago", "")} (it should run every 6 hours). Click <b>Sync now</b>; if that works but this keeps happening, check the Render logs.</div>}
      {needsConnect && <div className="igc-banner">Connect your Instagram (log in as the founder page) to start tracking posts.</div>}
      {acct?.needsReconnect && <div className="igc-banner">Instagram access expired — reconnect to keep syncing.</div>}
      {needsMetrics && <div className="igc-banner">Views, reach, saves, shares and watch time are off. Click <b>Reconnect</b> and approve the insights permission.</div>}

      {stat && (
        <div className="admindash-grid" style={{ marginTop: 16 }}>
          <div className="admindash-stat"><div className="admindash-stat-value">{compact(stat.followerCount)}</div><div className="admindash-stat-label">Followers</div></div>
          <div className="admindash-stat"><div className="admindash-stat-value">{num(summary.total)}</div><div className="admindash-stat-label">Posts tracked</div></div>
          <div className="admindash-stat"><div className="admindash-stat-value">{compact(stat.avgViews)}</div><div className="admindash-stat-label">Avg views / post</div></div>
          <div className="admindash-stat"><div className="admindash-stat-value">{compact(stat.totalViews)}</div><div className="admindash-stat-label">Total views</div></div>
          <div className="admindash-stat"><div className="admindash-stat-value">{(cats.viral || 0) + (cats.hit || 0)}</div><div className="admindash-stat-label">Viral + hits</div></div>
        </div>
      )}
      {history.length > 1 && (
        <div className="igc-trend"><span className="muted">Followers</span><Sparkline points={history.map((h) => h.followers)} /><span className="muted">Avg views</span><Sparkline points={history.map((h) => h.avgViews)} /></div>
      )}

      {hasPatterns && (
        <div className="igc-patterns">
          <div className="igc-patterns-title">What's working <span className="muted">— average views, from {an.analyzed} analyzed post{an.analyzed === 1 ? "" : "s"}</span></div>
          <div className="igc-patterns-grid">
            {[["hookStructure", "Hook style"], ["visualFormat", "Visual format"], ["contentType", "Content type"]].map(([f, title]) => patterns[f]?.length ? (
              <div key={f}><div className="igc-pat-h">{title}</div>
                {patterns[f].slice(0, 4).map((g) => <div key={g.name} className="igc-pat-row"><span>{g.name}</span><span className="muted">{g.count}×</span><b>{compact(g.avgViews)}</b></div>)}
              </div>
            ) : null)}
          </div>
        </div>
      )}

      <div className="igc-filters">
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="REELS">Reels</option><option value="">All posts</option><option value="FEED">Feed</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)}>{SORTS.map(([v, t]) => <option key={v} value={v}>Sort: {t}</option>)}</select>
        <button className={`igc-chip ${label === "" ? "on" : ""}`} onClick={() => setLabel("")}>All</button>
        {labels.map((l) => (
          <button key={l.label} className={`igc-chip ${label === l.label ? "on" : ""}`} onClick={() => setLabel(l.label)}>{l.label} <span className="muted">{l.count}</span></button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="admindash-empty">{needsConnect ? "Nothing yet." : "No posts yet — click “Sync now”."}</div>
      ) : (
        <div className="igc-table">
          <div className="igc-row igc-row--head">
            <span>Post</span><span>Views</span><span>Reach</span><span>Likes</span><span>Comments</span><span>Shares</span><span>Saves</span><span>Watch</span><span>Eng.</span><span>Outlier</span><span>Label</span><span />
          </div>
          {items.map((p) => {
            const m = (v) => (p.hasInsights ? num(v) : "—");
            return (
              <div key={p.mediaId}>
              <div
                className={`igc-row igc-row--click ${open === p.mediaId ? "igc-row--open" : ""} ${job?.running && job.current === p.mediaId ? "igc-row--busy" : ""}`}
                role="button" tabIndex={0} aria-expanded={open === p.mediaId} title="Click to see the analysis"
                onClick={() => toggleDetail(p)}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggleDetail(p); } }}
              >
                <div className="igc-post">
                  {p.thumbnailUrl ? <img src={p.thumbnailUrl} alt="" loading="lazy" /> : <div className="igc-thumb-empty" />}
                  <div><div className="igc-cap">{(p.caption || "(no caption)").replace(/\s+/g, " ").slice(0, 90)}</div><div className="muted igc-date">{day(p.postDate)}</div></div>
                  <a className="igc-ext" href={p.url} target="_blank" rel="noreferrer" title="Open on Instagram" onClick={(e) => e.stopPropagation()}>↗</a>
                </div>
                <span data-l="Views">{m(p.views)}</span><span data-l="Reach">{m(p.reach)}</span>
                <span data-l="Likes">{num(p.likes)}</span><span data-l="Comments">{num(p.comments)}</span>
                <span data-l="Shares">{m(p.shares)}</span><span data-l="Saves">{m(p.saves)}</span>
                <span data-l="Watch">{p.hasInsights && p.avgWatchTime ? `${p.avgWatchTime}s` : "—"}</span>
                <span data-l="Eng.">{p.hasInsights ? `${p.engagementRate}%` : "—"}</span>
                <span className={`igc-badge igc-badge--${p.outlierCategory}`} title={p.outlierScore ? `${p.outlierScore}× your average` : ""}>{p.hasInsights ? CATS[p.outlierCategory] : "—"}</span>
                <select value={p.label || ""} onClick={(e) => e.stopPropagation()} onChange={(e) => setPostLabel(p, e.target.value)}>
                  <option value="">unlabeled</option>
                  {[...new Set([...LABELS, p.label].filter(Boolean))].map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <span className="igc-more" aria-hidden="true">{open === p.mediaId ? "▴" : "▾"}</span>
              </div>
              {open === p.mediaId && (
                <div className="igc-detail">
                  {job?.running && job.current === p.mediaId ? <InlineProgress job={job} /> : !detail ? <span className="muted">Loading…</span> : !detail.isAnalyzed ? (
                    <div>
                      <span className="muted">Not analyzed yet.{detail.analysisError ? ` Last error: ${detail.analysisError}` : ""}</span>{" "}
                      {p.mediaType === "VIDEO" && (job?.running
                        ? <span className="igc-btn-busy igc-pill"><span className="igc-spin igc-spin--light" />{job.total > 1 ? "Waiting — another analysis is running" : "Starting…"}</span>
                        : <button className="igc-chip" onClick={() => startAnalyze([p.mediaId])}>Analyze this post</button>)}
                    </div>
                  ) : (
                    <>
                      <div className="igc-kv">
                        {[["Spoken hook", detail.spokenHook], ["Hook style", detail.hookStructure], ["Hook template", detail.hookFramework], ["Topic", [detail.topic, detail.topicSummary].filter(Boolean).join(" — ")],
                          ["Structure", detail.contentStructure], ["Type", detail.contentType], ["Call to action", detail.callToAction], ["On-screen text", detail.textHook],
                          ["Opening visual", detail.visualHook], ["Visual format", detail.visualFormat], ["Audio", detail.audioHook], ["Length", detail.duration ? `${detail.duration}s` : null]].filter(([, v]) => v).map(([k, v]) => (
                          <div key={k}><span className="muted">{k}</span><div>{v}</div></div>
                        ))}
                      </div>
                      {detail.transcript && <details className="igc-tx"><summary className="muted">Transcript</summary><p>{detail.transcript}</p></details>}
                    </>
                  )}
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
